import React, { useEffect } from "react";
import "./App.css";
import { Toaster, toast } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AllRoutes from "./routes/AllRoutes";
import AppErrorBoundary from "./components/common/AppErrorBoundary";
import CommandPalette from "./components/common/CommandPalette";
import useAxiosPrivate from "./hooks/useAxiosPrivate";
import useZustandStore from "./zustandStore/useZustandStore";
import LoginAPIs from "./apiRoutes/Login";
import ConfigAPIs from "./apiRoutes/Config";

const queryClient = new QueryClient();

function App() {
  const axiosPrivate = useAxiosPrivate();
  const setCurrentUser = useZustandStore((s) => s.setCurrentUser);
  const setConfig = useZustandStore((s) => s.setConfig);

  useEffect(() => {
    const path = window.location.pathname;
    if (path === "/login" || path === "/pending-approval") return;
    // On the public homepage, probe the session to redirect returning users to
    // the dashboard, but mark the request as `sent` so the 401 interceptor does
    // NOT trigger token refresh + logout redirect for anonymous visitors
    // (otherwise the public landing page can never render).
    const isPublicHome = path === "/";
    LoginAPIs.getProfile({
      axiosPrivate,
      config: isPublicHome ? { sent: true } : {},
    })
      .then(({ response }) => {
        const user = response?.data?.data?.user;
        if (user) setCurrentUser(user);
      })
      .catch(() => {
        if (!isPublicHome) toast.error("Failed to get profile");
      });
  }, [axiosPrivate, setCurrentUser]);

  // Config is public and needed on every screen, so it loads on every app start.
  useEffect(() => {
    ConfigAPIs.getConfig()
      .then(({ response }) => setConfig(response?.data ?? {}))
      .catch(() => toast.error("Failed to load AMS config"));
  }, [setConfig]);

  return (
    <div>
      {/* React query client */}
      <QueryClientProvider client={queryClient}>
        <AppErrorBoundary>
          <AllRoutes />
          <CommandPalette />
        </AppErrorBoundary>
      </QueryClientProvider>

      {/* Toaster on global Level */}
      <Toaster
        position="bottom-left"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          success: { duration: 2000 },
        }}
      />
    </div>
  );
}

export default App;
